import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import Tooltip from 'react-tooltip';
import { ListPagination, Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { Table } from 'src/common/components/Table';
import { OrbitIcons } from 'src/common/constants.';
import { useQuery } from 'src/common/hooks/useQuery';
import { deleteCampaignDetails } from 'src/tcr/api';
import { AccountProps } from 'src/tcr/types';

import { DeleteCampaignModal } from './DeleteCampaignDetailModal';

interface CampaignDetailListProps {
  query?: string;
  onEdit?: (item: any) => void;
  forwardRef?: any;
}

export const CampaignDetailList = ({
  query = '',
  onEdit = (item: any) => {},
  forwardRef,
}: CampaignDetailListProps): JSX.Element => {
  const modalRef = useRef<ModalRefProps>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, mutate } = useQuery(
    `/campaign_management/campaign_details?page=${currentPage}&search=${query}`
  );
  const accounts = (data as { results: Array<AccountProps> })?.results || [];
  const totalCount = data?.count;

  useImperativeHandle(forwardRef, () => ({
    reload: () => mutate(),
  }));

  const handleDelete = useCallback((id: string, deletable: boolean) => {
    modalRef.current?.show(id, deletable);
  }, []);

  const handleRemove = async (id: string): Promise<any> => {
    try {
      await deleteCampaignDetails(id);
      mutate();
      Toast.success('Campaign details has been removed successfully.');
    } catch (error) {
      Toast.error('Error occurred while remove campaign details.');
    }
    return;
  };

  const columns = useMemo(
    () => [
      { title: 'Name', index: 'name' },
      {
        title: 'Message flow',
        index: 'tcr_description',
        renderer: (row: any) => {
          let trimmedText = row?.tcr_description?.slice(0, 100);
          if (trimmedText.length < row?.tcr_message_flow?.length) trimmedText += '...';
          return (
            <>
              <div data-for={`data-tooltip-${row.id}-description`} data-tip>
                {trimmedText}
              </div>
              <Tooltip id={`data-tooltip-${row.id}-description`}>
                <div className="max-w-2xl text-white-0.0">{row?.tcr_description}</div>
              </Tooltip>
            </>
          );
        },
      },
      {
        title: 'Description',
        index: 'tcr_message_flow',
        renderer: (row: any) => {
          let trimmedText = row?.tcr_message_flow?.slice(0, 100);
          if (trimmedText.length < row?.tcr_message_flow?.length) trimmedText += '...';
          return (
            <>
              <div data-for={`data-tooltip-${row.id}-message-flow`} data-tip>
                {trimmedText}
              </div>
              <Tooltip id={`data-tooltip-${row.id}-message-flow`}>
                <div className="max-w-2xl text-white-0.0">{row?.tcr_message_flow}</div>
              </Tooltip>
            </>
          );
        },
      },
      {
        title: '',
        index: '',
        renderer: (row: any) => (
          <div className="flex justify-end">
            <button
              className="btn bg-red-1.0 text-white-0.0 mr-2"
              onClick={() => handleDelete(row?.id, !row.accounts.length)}
            >
              <Icon name={OrbitIcons.Trash} />
            </button>
            <button className="btn bg-gray-0.0" onClick={() => onEdit(row)}>
              <Icon name={OrbitIcons.Pencil} />
            </button>
          </div>
        ),
      },
    ],
    [onEdit, handleDelete]
  );

  if (!accounts?.length)
    return (
      <div className="p-4 bg-gray-0.0 rounded-md">
        Not found campaign details to show. Add someone
      </div>
    );

  return (
    <>
      <div className="scrollbar-thin flex-1 overflow-auto">
        <Table<AccountProps>
          data={accounts}
          columns={columns}
          loading={isLoading}
          columnsClassName="break-spaces"
        />
      </div>
      {totalCount > 20 && (
        <ListPagination
          currentPage={currentPage}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
        />
      )}
      <DeleteCampaignModal ref={modalRef} onAccept={handleRemove} />
    </>
  );
};
